<script>
import { fade } from "svelte/transition";
import { toast } from "@zerodevx/svelte-toast";
import { hideModal, modalState } from "@app/store/modalStore.svelte.js";
import { eventGroupState } from "@app/store/eventGroupStore.svelte.js";
import {
    applyLiveCaptionPlatformDefaults,
    liveCaptionState,
    saveLiveCaptionSettings,
    testLiveCaptionConnection,
} from "@app/store/liveCaptionStore.svelte.js";
import { getPlatformConfig, PLATFORM_OPTIONS } from "@app/lib/liveCaption/index.js";
import * as m from "@app/paraglide/messages.js";

let activeTab = $state("connection");
let testingConnection = $state(false);

let platformConfig = $derived(getPlatformConfig(liveCaptionState.platform));
let maxCharsValue = $derived([32, 42, 72].includes(Number(liveCaptionState.maxChars)) ? String(liveCaptionState.maxChars) : "custom");

const YOUTUBE_REGIONS = [
    { value: 1, label: () => m.live_caption_youtube_region_1() },
    { value: 2, label: () => m.live_caption_youtube_region_2() },
    { value: 3, label: () => m.live_caption_youtube_region_3() },
    { value: 4, label: () => m.live_caption_youtube_region_4() },
    { value: 5, label: () => m.live_caption_youtube_region_5() },
    { value: 6, label: () => m.live_caption_youtube_region_6() },
    { value: 7, label: () => m.live_caption_youtube_region_7() },
    { value: 8, label: () => m.live_caption_youtube_region_8() },
    { value: 9, label: () => m.live_caption_youtube_region_9() },
];

function setActiveTab(tab) {
    activeTab = tab;
}

function handlePlatformChange(e) {
    applyLiveCaptionPlatformDefaults(e.target.value);
}

function handleMaxCharsChange(e) {
    if (e.target.value === "custom") {
        liveCaptionState.maxChars = Number(liveCaptionState.customMaxChars) || platformConfig.defaultMaxChars;
    } else {
        liveCaptionState.maxChars = Number(e.target.value);
    }
}

function saveSettings() {
    saveLiveCaptionSettings();
    hideModal();
}

async function handleTestConnection() {
    testingConnection = true;

    try {
        await testLiveCaptionConnection();
        toast.push(m.live_caption_test_success(), { classes: ["toast-success"] });
    } catch (err) {
        toast.push(err.message, { classes: ["toast-danger"] });
    } finally {
        testingConnection = false;
    }
}
</script>

<div
    transition:fade={{ duration: 100 }}
    class="modal fade {modalState.selected === 'liveCaptionSettings' ? 'show d-block' : ''}"
    role="dialog"
    aria-labelledby="liveCaptionSettingsTitle"
    aria-modal="true"
    tabindex="-1"
    id="LiveCaptionSettingsModal">
    <div class="modal-dialog modal-dialog-centered modal-lg modal-dialog-scrollable" role="document">
        <div class="modal-content">
            <div class="modal-header">
                <h4 class="modal-title" id="liveCaptionSettingsTitle">{m.live_caption_settings_title()}</h4>
                <button type="button" class="btn-close" aria-label={m.close_dialog()} onclick={hideModal}></button>
            </div>
            <div class="modal-body">
                <ul class="nav nav-tabs mb-3" role="tablist" aria-label={m.live_caption_settings_tabs_label()}>
                    <li class="nav-item" role="presentation">
                        <button
                            class="nav-link {activeTab === 'connection' ? 'active' : ''}"
                            id="liveCaptionConnectionTab"
                            type="button"
                            role="tab"
                            aria-controls="liveCaptionConnectionPanel"
                            aria-selected={activeTab === "connection"}
                            onclick={() => setActiveTab("connection")}>
                            <i class="bi bi-broadcast" aria-hidden="true"></i> {m.live_caption_connection_tab()}
                        </button>
                    </li>
                    <li class="nav-item" role="presentation">
                        <button
                            class="nav-link {activeTab === 'caption' ? 'active' : ''}"
                            id="liveCaptionCaptionTab"
                            type="button"
                            role="tab"
                            aria-controls="liveCaptionCaptionPanel"
                            aria-selected={activeTab === "caption"}
                            onclick={() => setActiveTab("caption")}>
                            <i class="bi bi-badge-cc" aria-hidden="true"></i> {m.live_caption_caption_settings_tab()}
                        </button>
                    </li>
                    <li class="nav-item" role="presentation">
                        <button
                            class="nav-link {activeTab === 'display' ? 'active' : ''}"
                            id="liveCaptionDisplayTab"
                            type="button"
                            role="tab"
                            aria-controls="liveCaptionDisplayPanel"
                            aria-selected={activeTab === "display"}
                            onclick={() => setActiveTab("display")}>
                            <i class="bi bi-fonts" aria-hidden="true"></i> {m.live_caption_display_tab()}
                        </button>
                    </li>
                </ul>

                <div class="tab-content">
                    <div
                        class="tab-pane fade {activeTab === 'connection' ? 'show active' : ''}"
                        id="liveCaptionConnectionPanel"
                        role="tabpanel"
                        aria-labelledby="liveCaptionConnectionTab"
                        tabindex="0">
                        <div class="mb-3">
                            <label class="form-label" for="liveCaptionPlatform">{m.live_caption_platform()}</label>
                            <select class="form-select" id="liveCaptionPlatform" value={liveCaptionState.platform} onchange={handlePlatformChange}>
                                {#each PLATFORM_OPTIONS as platform (platform.id)}
                                    <option value={platform.id}>{platform.label}</option>
                                {/each}
                            </select>
                        </div>

                        <div class="mb-3">
                            <label class="form-label" for="liveCaptionEndpoint">{platformConfig.endpointLabel}</label>
                            <input
                                class="form-control"
                                id="liveCaptionEndpoint"
                                type="text"
                                bind:value={liveCaptionState.endpoint}
                                placeholder={platformConfig.endpointPlaceholder}
                                aria-describedby="liveCaptionEndpointHelp" />
                            <div id="liveCaptionEndpointHelp" class="form-text">{platformConfig.endpointHelp}</div>
                        </div>

                        <div class="mb-3">
                            <label class="form-label" for="liveCaptionAuth">{platformConfig.authLabel}</label>
                            <input
                                class="form-control"
                                id="liveCaptionAuth"
                                type={liveCaptionState.platform === "obs" ? "password" : "text"}
                                bind:value={liveCaptionState.auth}
                                placeholder={m.live_caption_auth_placeholder()} />
                            <div class="form-text">{m.live_caption_auth_help()}</div>
                        </div>

                        {#if liveCaptionState.platform === "youtube"}
                            <div class="alert alert-warning" role="alert">
                                <strong>{m.live_caption_youtube_warning_title()}</strong>
                                <div>{m.live_caption_youtube_warning_body()}</div>
                            </div>
                        {/if}

                        <button
                            class="btn btn-outline-primary"
                            type="button"
                            disabled={testingConnection}
                            onclick={handleTestConnection}>
                            {#if testingConnection}
                                <span class="spinner-border spinner-border-sm me-2" aria-hidden="true"></span>
                            {/if}
                            {m.live_caption_test_connection()}
                        </button>
                    </div>

                    <div
                        class="tab-pane fade {activeTab === 'caption' ? 'show active' : ''}"
                        id="liveCaptionCaptionPanel"
                        role="tabpanel"
                        aria-labelledby="liveCaptionCaptionTab"
                        tabindex="0">
                        <div class="row">
                            <div class="col-md-6 mb-3">
                                <label class="form-label" for="liveCaptionRows">{m.live_caption_rollup_rows()}</label>
                                <select class="form-select" id="liveCaptionRows" bind:value={liveCaptionState.rows}>
                                    <option value={2}>2</option>
                                    <option value={3}>3</option>
                                    <option value={4}>4</option>
                                </select>
                            </div>
                            <div class="col-md-6 mb-3">
                                <label class="form-label" for="liveCaptionMaxChars">{m.live_caption_max_chars()}</label>
                                <select class="form-select" id="liveCaptionMaxChars" value={maxCharsValue} onchange={handleMaxCharsChange}>
                                    <option value="32">32</option>
                                    <option value="42">42</option>
                                    <option value="72">72</option>
                                    <option value="custom">{m.live_caption_custom_max_chars()}</option>
                                </select>
                            </div>
                        </div>

                        {#if maxCharsValue === "custom"}
                            <div class="mb-3">
                                <label class="form-label" for="liveCaptionCustomMaxChars">{m.live_caption_custom_max_chars()}</label>
                                <input
                                    class="form-control"
                                    id="liveCaptionCustomMaxChars"
                                    type="number"
                                    min="1"
                                    bind:value={liveCaptionState.customMaxChars}
                                    oninput={() => liveCaptionState.maxChars = Number(liveCaptionState.customMaxChars) || platformConfig.defaultMaxChars} />
                            </div>
                        {/if}

                        <div class="mb-3 form-check form-switch">
                            <input class="form-check-input" type="checkbox" id="liveCaptionAutoWrap" bind:checked={liveCaptionState.autoWrap} />
                            <label class="form-check-label" for="liveCaptionAutoWrap">{m.live_caption_auto_wrap()}</label>
                        </div>

                        {#if platformConfig.supportsChannel}
                            <div class="mb-3">
                                <label class="form-label" for="liveCaptionChannel">{m.live_caption_channel()}</label>
                                <select class="form-select" id="liveCaptionChannel" bind:value={liveCaptionState.captionChannel}>
                                    <option value="CC1">CC1</option>
                                    <option value="CC2">CC2</option>
                                    <option value="CC3">CC3</option>
                                    <option value="CC4">CC4</option>
                                </select>
                            </div>
                        {/if}

                        {#if platformConfig.supportsLatency}
                            <div class="mb-3">
                                <label class="form-label" for="liveCaptionLatency">
                                    {m.live_caption_latency_offset({ value: liveCaptionState.latencyOffset })}
                                </label>
                                <input
                                    class="form-range"
                                    id="liveCaptionLatency"
                                    type="range"
                                    min="-5"
                                    max="5"
                                    step="0.5"
                                    bind:value={liveCaptionState.latencyOffset} />
                            </div>
                        {/if}

                        {#if platformConfig.supportsRegion}
                            <div class="mb-3">
                                <div class="form-label" id="liveCaptionYoutubeRegionLabel">{m.live_caption_youtube_region()}</div>
                                <div id="liveCaptionYoutubeRegionHelp" class="form-text mb-2">{m.live_caption_youtube_region_help()}</div>
                                <div
                                    class="row g-2"
                                    role="group"
                                    aria-labelledby="liveCaptionYoutubeRegionLabel"
                                    aria-describedby="liveCaptionYoutubeRegionHelp">
                                    {#each YOUTUBE_REGIONS as region (region.value)}
                                        <div class="col-4">
                                            <button
                                                class="btn w-100 {liveCaptionState.youtubeRegion === region.value ? 'btn-primary' : 'btn-outline-primary'}"
                                                type="button"
                                                aria-pressed={liveCaptionState.youtubeRegion === region.value}
                                                onclick={() => liveCaptionState.youtubeRegion = region.value}>
                                                {region.label()}
                                            </button>
                                        </div>
                                    {/each}
                                </div>
                            </div>
                        {/if}

                        <div class="mb-3">
                            <label class="form-label" for="liveCaptionEventGroup">{m.live_caption_target_event_group()}</label>
                            <select class="form-select" id="liveCaptionEventGroup" bind:value={liveCaptionState.targetEventGroup}>
                                <option value="active">{m.live_caption_active_event_group()}</option>
                                {#each eventGroupState as eventGroup, index (eventGroup.id || index)}
                                    <option value={index}>{eventGroup.name || `${m.live_caption_event_group()} ${index + 1}`}</option>
                                {/each}
                            </select>
                        </div>
                    </div>

                    <div
                        class="tab-pane fade {activeTab === 'display' ? 'show active' : ''}"
                        id="liveCaptionDisplayPanel"
                        role="tabpanel"
                        aria-labelledby="liveCaptionDisplayTab"
                        tabindex="0">
                        <div class="mb-3">
                            <label class="form-label" for="liveCaptionFontSize">{m.live_caption_font_size()}</label>
                            <input
                                class="form-control"
                                id="liveCaptionFontSize"
                                type="number"
                                min="10"
                                max="48"
                                bind:value={liveCaptionState.fontSize} />
                        </div>
                        <div class="mb-3 form-check form-switch">
                            <input class="form-check-input" type="checkbox" id="liveCaptionShowCounter" bind:checked={liveCaptionState.showCharCounter} />
                            <label class="form-check-label" for="liveCaptionShowCounter">{m.live_caption_show_counter()}</label>
                        </div>
                        <div class="mb-3 form-check form-switch">
                            <input class="form-check-input" type="checkbox" id="liveCaptionShowTimer" bind:checked={liveCaptionState.showSessionTimer} />
                            <label class="form-check-label" for="liveCaptionShowTimer">{m.live_caption_show_timer()}</label>
                        </div>
                    </div>
                </div>
            </div>
            <div class="modal-footer">
                <button type="button" class="btn btn-outline-secondary" onclick={hideModal}>{m.cancel()}</button>
                <button type="button" class="btn btn-primary" onclick={saveSettings}>{m.save()}</button>
            </div>
        </div>
    </div>
</div>
